import React from 'react';
import { Box, Grid } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminAPI } from '../../../utils/api';
import EngagementView from './EngagementView';

const timeAgo = (iso) => {
  if (!iso) return 'unknown';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const mapIntelRunner = (row) => {
  const parts = [];
  if (row.email) parts.push(row.email);
  parts.push(`${row.views || 0} views`);
  parts.push(`last ${timeAgo(row.lastRunAt || row.firstRunAt)}`);
  return {
    id: row.sleeperUsername,
    title: row.sleeperUsername ? `@${row.sleeperUsername}` : 'Unknown',
    subtitle: parts.join(' · '),
    signedUp: !!row.signedUp,
    runs: row.runs,
  };
};

const ManagerIntelAnalyticsTab = ({ metrics, timeSeries }) => {
  return (
    <Box>
      {/* Engagement: daily/weekly/monthly + signed-up conversion + who */}
      <Box sx={{ marginBottom: '32px' }}>
        <EngagementView
          accent="#8B5CF6"
          fetchStats={adminAPI.getManagerIntelEngagement}
          runWord="profiles"
          runnerWord="managers"
          mapRunner={mapIntelRunner}
        />
      </Box>

      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        <Grid item xs={12} md={3}>
          <StatBox label="Total Syncs" value={metrics.managerIntel.totalSyncs} color="#8B5CF6" icon="📊" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatBox label="Unique Profiles" value={metrics.managerIntel.uniqueUsers} color="#3B82F6" icon="👤" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatBox label="Syncs Today" value={metrics.managerIntel.today} color="#10B981" icon="🔄" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatBox label="Error Count" value={metrics.managerIntel.errorCount} color="#EF4444" icon="⚠️" />
        </Grid>
      </Grid>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#8B5CF6' }}>
          Sync Activity (14 Days)
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={timeSeries.syncs}>
            <defs>
              <linearGradient id="syncGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="date"
              stroke="#64748B"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                background: '#1E293B',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#F1F5F9',
              }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#8B5CF6"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#syncGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#3B82F6' }}>
          Top Synced Profiles
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {metrics.managerIntelProfiles.slice(0, 10).map((profile, index) => (
            <Box
              key={index}
              component="a"
              href={`/manager-intel/${profile.sleeperUsername}`}
              target="_blank"
              sx={{
                background: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#3B82F6',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                #{index + 1}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '15px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                  {profile.sleeperUsername}
                </Box>
                <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                  Last synced: {profile.lastRefreshedAt ? new Date(profile.lastRefreshedAt).toLocaleDateString() : 'Never'}
                </Box>
              </Box>
              <Box
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: profile.status === 'complete' ? '#10B981' : profile.status === 'error' ? '#EF4444' : '#F59E0B',
                  textTransform: 'uppercase',
                }}
              >
                {profile.status}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const StatBox = ({ label, value, color, icon }) => (
  <Box
    sx={{
      background: '#1E293B',
      border: `2px solid ${color}`,
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}44`,
      },
    }}
  >
    <Box sx={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</Box>
    <Box sx={{ fontSize: '48px', fontWeight: 700, color, marginBottom: '8px' }}>
      {value}
    </Box>
    <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>
      {label}
    </Box>
  </Box>
);

export default ManagerIntelAnalyticsTab;
